import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { IUser } from '../lib/interfaces/User';
import SearchIcon from './icons/SearchIcon';

function isLastItemOfList<T>(arr: T[], index: number) {
    return arr.length - 1 === index;
}

interface RenderTextProps {
    isHighlighted: boolean;
    text: string;
}

const RenderText = ({ isHighlighted, text }: RenderTextProps) => {
    return isHighlighted ? (
        <StyledHighlightedText>{text}</StyledHighlightedText>
    ) : (
        <StyledNormalText>{text}</StyledNormalText>
    );
};

interface SearchActionMenuBarProps {
    query: string;
    text: string;
}

const SearchActionMenuBar = ({ text, query }: SearchActionMenuBarProps) => {
    return (
        <StyledActionMenuBarContainer>
            <StyledIconWrapper>{<SearchIcon />}</StyledIconWrapper>
            <StyledSearchBar>
                {text.split(' ').map((t, i) => (
                    <RenderText
                        key={`search-display-text-span-${t}`}
                        text={
                            isLastItemOfList(text.split(' '), i) ? t : t + ' '
                        }
                        isHighlighted={query
                            .split(' ')
                            .some((v) => v.includes(t))}
                    />
                ))}
            </StyledSearchBar>
        </StyledActionMenuBarContainer>
    );
};

interface SearchResultsDisplayProps {
    query: string;
    users: IUser[];
    hide: () => void;
}

const SearchResultsDisplay = ({
    query,
    users,
    hide,
}: SearchResultsDisplayProps) => {
    return (
        <StyledSearchResultsDisplay>
            {users.map((user) => (
                <StyledLink
                    key={`user-search-result-${user._id}`}
                    to={`/search?q=${user.displayName}`}
                    onClick={hide}
                >
                    <SearchActionMenuBar
                        query={query}
                        text={user.displayName}
                    />
                </StyledLink>
            ))}
            <StyledSpecialLink to={`/search?q=${query}`} onClick={hide}>
                <SearchActionMenuBar
                    query={query}
                    text={`Search for ${query}`}
                />
            </StyledSpecialLink>
        </StyledSearchResultsDisplay>
    );
};

const StyledSearchResultsDisplay = styled.div`
    background-color: var(--secondary-background-color);
    display: flex;
    flex-direction: column;
    width: 344px;
    max-height: 380px;
    position: absolute;
    box-shadow: var(--search-box-shadow);
    overflow-y: scroll;

    border-radius: 0 0 10px 10px;

    left: 0px;
    top: 54px;

    box-sizing: border-box;
    padding: 6px 12px;
`;

const StyledActionMenuBarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;

    padding: 6px;

    border-radius: 6px;

    cursor: pointer;

    &:hover {
        background-color: var(--hover-background-color);
    }
`;

const StyledIconWrapper = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: grid;
    place-content: center;

    background-color: var(--web-wash-background-color);
`;

const StyledSearchBar = styled.p`
    margin: 0;
    font-size: 15px;

    font-family: Roboto, sans-serif;
`;

const StyledHighlightedText = styled.span`
    font-size: 15px;
`;

const StyledNormalText = styled.span`
    font-weight: 600;
`;

const StyledSpecialLink = styled(Link)`
    color: var(--primary-color);
    text-decoration: none;
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: inherit;
`;

export default SearchResultsDisplay;
